// The Claude models SMART can name, price and select: the browser's one reading of
// `shared/specs/claude-models.json`, the mirror of `ClaudeModel` and `PRICED_MODELS` in
// shared/src/claude.rs (`the_json_catalog_mirrors_this_one` fails the Rust build on any
// difference). Labels, list prices and default efforts are read here and nowhere else in
// the browser: the config editor's model select, the Spend panel's model rows and the
// Daily Briefing's "written by" all name a model through this module.

import catalog from '@shared/specs/claude-models.json';

/** USD per million tokens, list price, standard tier. */
export interface ClaudePrice {
  input: number;
  cache_write_5m: number;
  cache_write_1h: number;
  cache_read: number;
  output: number;
}

export interface ClaudeCatalogModel {
  /** The Claude API model id (`claude-sonnet-5-5`). */
  id: string;
  /** What a person reads (`Claude Sonnet 5.5`). */
  label: string;
  /** False for a fallback target: priced because a fallback iteration is billed, never offered. */
  selectable: boolean;
  /** The effort the model uses when none is sent; null on a fallback target. */
  default_effort: string | null;
  accepted_efforts: readonly string[] | null;
  max_output_tokens: number | null;
  /** Whether a refusal may be answered on Claude's recommended fallback model. */
  server_side_fallback: boolean | null;
  price: ClaudePrice;
  /** A dearer tier for a prompt over `above_prompt_tokens` (Haiku only, today). */
  long_prompt: { above_prompt_tokens: number; price: ClaudePrice } | null;
}

/** Every priced model, selectable ones first, in the catalog's order. */
export const CLAUDE_MODELS: readonly ClaudeCatalogModel[] = catalog.models;

/** The models an Admin may pick for a task, in the order the select offers them. */
export const SELECTABLE_CLAUDE_MODELS: readonly ClaudeCatalogModel[] =
  CLAUDE_MODELS.filter((model) => model.selectable);

/** The day the list prices were read (`YYYY-MM-DD`). */
export const CLAUDE_PRICES_AS_OF: string = catalog.prices_as_of;

const BY_ID = new Map(CLAUDE_MODELS.map((model) => [model.id, model]));

export function claudeModel(id: string): ClaudeCatalogModel | undefined {
  return BY_ID.get(id);
}

/** The catalog label for a priced model id, or null for an id the catalog does not price. */
export function claudeModelLabel(id: string): string | null {
  return BY_ID.get(id)?.label ?? null;
}

/** `$4`, `$0.10`, `$2.50`: a per-million price as the help prints it. */
export function perMillionUsd(price: number): string {
  return Number.isInteger(price) ? `$${price}` : `$${price.toFixed(2)}`;
}

/**
 * One model's line in the config editor: its price, its long-prompt tier, the effort it
 * uses when none is chosen and, when it has none, that a refusal is not retried on a
 * fallback model.
 */
export function claudeModelSummary(model: ClaudeCatalogModel): string {
  const { input, output } = model.price;
  const parts = [`${perMillionUsd(input)} input / ${perMillionUsd(output)} output per million tokens`];
  if (model.long_prompt) {
    const long = model.long_prompt;
    parts[0] += ` (${perMillionUsd(long.price.input)} / ${perMillionUsd(long.price.output)} for a prompt over ${long.above_prompt_tokens.toLocaleString('en-US')} tokens)`;
  }
  if (model.default_effort) parts.push(`Default effort ${model.default_effort}`);
  if (model.server_side_fallback === false) parts.push('No refusal fallback');
  return `${parts.join('. ')}.`;
}

/** The config editor's model select: every selectable model, described. */
export const CLAUDE_MODEL_SELECT_OPTIONS: ReadonlyArray<{ value: string; label: string; description: string }> =
  SELECTABLE_CLAUDE_MODELS.map((model) => ({
    value: model.id,
    label: model.label,
    description: claudeModelSummary(model),
  }));
